import {
  Grid,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  TextField,
} from "@material-ui/core";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { DeleteOutline } from "@material-ui/icons";
import AddRowButton from "components/common/AddRowButton";
import ButtonController from "components/request-document/components/ButtonController";
import {
  StyledTableCell,
  useStyles,
} from "components/request-document/components/Commons";
import { useFormik } from "formik";
import { M24_CONFIG_DOC, RoleId } from "libs/configs/constant";
import useModalLoading from "libs/hooks/useModalLoading";
import {
  defaultShareholders,
  getDefaultRow,
  getErrorMessage,
  isError,
  mapShareholders,
} from "libs/utils/form";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { canEditForm } from "redux/modules/authSlice";
import { selectM24SwitchMode } from "redux/modules/m24SwitchModeSlice";
import { getMasterData, selectMasterData } from "redux/modules/masterSlice";
import {
  addSmartCompany,
  addSmartCompanySlice,
  selectAddSmartCompany,
  selectSmartCompany,
} from "redux/modules/smartCompanySlice";
import * as yup from "yup";

const useStyle = makeStyles(() => ({
  container: {
    border: "0.2px solid rgba(0, 0, 0, 0.23);",
    padding: 28,
  },
  tableCell: {
    padding: 24,
  },
  gridContainer: {
    padding: "24px 38px",
  },
  textTitle: {
    fontSize: 22,
    color: "#172B4D",
    padding: 0,
    margin: 0,
  },
  textLabelBold: {
    fontSize: 22,
    color: "#1094D8",
    padding: 0,
    margin: 0,
  },
  textLabelRedBold: {
    fontSize: 22,
    color: "#FF0000",
    fontWeight: "normal",
    padding: 0,
    margin: 0,
  },
  textLabelGreenBold: {
    fontSize: 22,
    color: "#2DCE98",
    fontWeight: "normal",
    padding: 0,
    margin: 0,
  },
  textTotalTitle: {
    fontSize: 18,
    padding: 0,
    margin: 0,
    color: "#565656",
    textTransform: "uppercase",
  },
  textTotalValue: {
    color: "#2DCE98",
  },
}));

export default function CompanyInfo({ onNext }) {
  const thaiNationalityID = "444094be-31f8-59ad-7616-f7e1833038dc";
  const classes = useStyle();
  const classesField = useStyles();
  const dispatch = useDispatch();
  const { showLoading, hideLoading } = useModalLoading();

  const [isSaveDraft, setSaveDraft] = useState(false);

  const masterData = useSelector(selectMasterData);
  const canEdit = useSelector(canEditForm);
  const addSmartCompanyState = useSelector(selectAddSmartCompany);
  const smartCompanyState = useSelector(selectSmartCompany);
  const userProfile = useSelector((state) => state.user.profile);
  const m24SwitchMode = useSelector(selectM24SwitchMode);

  const smartCompanyData = get(smartCompanyState, "data", {});
  const nationalityConfig = get(masterData, "nationalityConfig", []);

  const shouldAllowWithoutDirector =
    M24_CONFIG_DOC.INV.CONDITION_TYPE === m24SwitchMode;
  const isDirector =
    shouldAllowWithoutDirector || userProfile?.roleId === RoleId.Director;

  useEffect(() => {
    dispatch(getMasterData({ nationalityConfig: true }));
  }, [dispatch]);

  useEffect(() => {
    if (!addSmartCompanyState.isLoading) {
      if (addSmartCompanyState.isSuccess) {
        hideLoading();
        dispatch(addSmartCompanySlice.actions.clearAsyncState());

        if (!isSaveDraft) {
          onNext();
        }
      } else if (addSmartCompanyState.isFailure) {
        hideLoading();
        alert(addSmartCompanyState.error?.message);
        dispatch(addSmartCompanySlice.actions.clearAsyncState());
      }
    }
  }, [addSmartCompanyState, dispatch, hideLoading, isSaveDraft]);

  const callAPISubmit = async (values) => {
    showLoading();

    const sharedholders = formik.values.sharedholders
      .filter((v) => {
        if (!v.status && !v.shareholderID) {
          return false;
        }
        if (!v.shareholderID && v.shareholderPercent <= 0) {
          return false;
        }
        return true;
      })
      .reduce((p, c) => {
        if (p.some((v) => v.nationalityID === c.nationalityID)) {
          const newArr = p.map((v) => {
            if (v.nationalityID === c.nationalityID) {
              return {
                ...v,
                shareholderPercent:
                  (v.shareholderPercent || 0) + (c.shareholderPercent || 0),
              };
            }
            return v;
          });

          if (c.shareholderID) {
            return [...newArr, { ...c, status: false, shareholderPercent: 0 }];
          }

          return newArr;
        }

        return [...p, c];
      }, []);

    const prepareValue = {
      ...values,
      sharedholders,
    };
    dispatch(addSmartCompany(prepareValue));
  };

  const validationSchema = yup.object({});
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      smartCompanyID: get(smartCompanyData, "smartCompanyID", null),
      taxID: get(smartCompanyData, "taxID", ""),
      companyNameEN: get(smartCompanyData, "companyNameEN", ""),
      companyAddress: get(smartCompanyData, "companyAddress", 0),
      numberForeignEmployee: get(smartCompanyData, "numberForeignEmployee", 0),
      numberThaiEmployee: get(smartCompanyData, "numberThaiEmployee", 0),
      numberEmployee: get(smartCompanyData, "numberEmployee", 0),
      sharedholders: getDefaultRow(smartCompanyData, "sharedholders", {
        ...defaultShareholders,
        nationalityID: thaiNationalityID,
      }).map(mapShareholders),
    },
    validationSchema,
    onSubmit: async (values) => {
      setSaveDraft(false);
      callAPISubmit(values);
    },
  });

  useEffect(() => {
    formik.setFieldValue(
      "numberEmployee",
      formik.values.numberForeignEmployee + formik.values.numberThaiEmployee
    );
  }, [formik.values.numberForeignEmployee, formik.values.numberThaiEmployee]);

  const Sharedholders = formik.values.sharedholders;
  const thaiSharedholdersItemIndex = formik.values.sharedholders.findIndex(
    (v) => v.nationalityID === thaiNationalityID
  );
  const thaiSharedholders = formik.values.sharedholders.find(
    (v) => v.nationalityID === thaiNationalityID
  );
  const firstSharedholdersItemIndex = Sharedholders.findIndex((v) => v.status);

  const currentShareTotal = Sharedholders.filter((v) => v.status).reduce(
    (p, c) => p + (c.shareholderPercent || 0),
    0
  );
  const isCurrentShareTotalValid = currentShareTotal === 100;

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <TableContainer>
          <Table
            className={classesField.tableBodyFull}
            aria-label="simple table"
          >
            <TableBody>
              <TableRow>
                <StyledTableCell className={classes.tableCell}>
                  <h4 className={classes.textTitle}>Company information</h4>
                  <Grid container className={classes.gridContainer} spacing={4}>
                    <Grid item xs={6}>
                      <div className={classesField.cellContent}>
                        <div className={classesField.wrapField}>
                          <InputLabel>Tax ID</InputLabel>
                          <TextField
                            disabled={!canEdit || !isDirector}
                            className={classesField.textField}
                            variant="outlined"
                            name="taxID"
                            value={formik.values.taxID}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={isError("taxID", formik)}
                            helperText={getErrorMessage("taxID", formik)}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classesField.cellContent}>
                        <div className={classesField.wrapField}>
                          <InputLabel>Name of institution / company</InputLabel>
                          <TextField
                            disabled={!canEdit || !isDirector}
                            className={classesField.textField}
                            variant="outlined"
                            name="companyNameEN"
                            value={formik.values.companyNameEN}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={isError("companyNameEN", formik)}
                            helperText={getErrorMessage(
                              "companyNameEN",
                              formik
                            )}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div className={classesField.cellContent}>
                        <div className={classesField.wrapField}>
                          <InputLabel>Address</InputLabel>
                          <TextField
                            disabled={!canEdit || !isDirector}
                            className={classesField.textField}
                            variant="outlined"
                            name="companyAddress"
                            value={formik.values.companyAddress}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={isError("companyAddress", formik)}
                            helperText={getErrorMessage(
                              "companyAddress",
                              formik
                            )}
                            multiline
                            rows={5}
                            rowsMin={5}
                            rowsMax={5}
                          />
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell className={classes.tableCell}>
                  <h4 className={classes.textTitle}>Shareholders</h4>
                  <Grid container className={classes.gridContainer} spacing={4}>
                    <Grid item xs={12} style={{ paddingLeft: 0 }}>
                      <h5
                        className={
                          isCurrentShareTotalValid
                            ? classes.textLabelGreenBold
                            : classes.textLabelRedBold
                        }
                      >
                        Current Share total : {currentShareTotal}%
                      </h5>
                    </Grid>
                    <Grid item xs={5}>
                      <div className={classesField.cellContent}>
                        <div className={classesField.wrapField}>
                          <InputLabel>Thai shareholders (%)</InputLabel>
                          <TextField
                            disabled={!canEdit || !isDirector}
                            className={classesField.textField}
                            variant="outlined"
                            type="number"
                            name={`sharedholders.${thaiSharedholdersItemIndex}.shareholderPercent`}
                            value={thaiSharedholders?.shareholderPercent}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={isError(
                              `sharedholders.${thaiSharedholdersItemIndex}.shareholderPercent`,
                              formik
                            )}
                            helperText={getErrorMessage(
                              `sharedholders.${thaiSharedholdersItemIndex}.shareholderPercent`,
                              formik
                            )}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={12} style={{ paddingBottom: 0 }}>
                      <h5 className={classes.textLabelBold}>
                        Foreign shareholders (%)
                      </h5>
                    </Grid>
                    <Grid container item spacing={2}>
                      {Sharedholders.map((sharedholders, index) => {
                        if (
                          !sharedholders.status ||
                          sharedholders.nationalityID === thaiNationalityID
                        ) {
                          return;
                        }
                        return (
                          <React.Fragment key={index}>
                            <Grid item xs={5}>
                              <InputLabel>Nationality</InputLabel>
                              <FormControl
                                disabled={!canEdit || !isDirector}
                                variant="outlined"
                                className={classesField.formControl}
                                style={{
                                  margin: 0,
                                  marginTop: 8,
                                }}
                                fullWidth
                                error={isError(
                                  `sharedholders.${index}.nationalityID`,
                                  formik
                                )}
                              >
                                <Select
                                  disabled={!canEdit || !isDirector}
                                  name={`sharedholders.${index}.nationalityID`}
                                  value={get(
                                    formik.values,
                                    `sharedholders.${index}.nationalityID`,
                                    ""
                                  )}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                >
                                  {nationalityConfig.map((nationality) => (
                                    <MenuItem
                                      value={nationality.nationalityID}
                                      key={nationality.nationalityID}
                                    >
                                      {nationality.nationalityName}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <FormHelperText
                                  error={isError(
                                    `sharedholders.${index}.nationalityID`,
                                    formik
                                  )}
                                >
                                  {getErrorMessage(
                                    `sharedholders.${index}.nationalityID`,
                                    formik
                                  )}
                                </FormHelperText>
                              </FormControl>
                            </Grid>
                            <Grid item xs={5}>
                              <div className={classesField.cellContent}>
                                <div className={classesField.wrapField}>
                                  <InputLabel>Percent</InputLabel>
                                  <TextField
                                    disabled={!canEdit || !isDirector}
                                    className={classesField.textField}
                                    variant="outlined"
                                    type="number"
                                    name={`sharedholders.${index}.shareholderPercent`}
                                    value={sharedholders.shareholderPercent}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={isError(
                                      `sharedholders.${index}.shareholderPercent`,
                                      formik
                                    )}
                                    helperText={getErrorMessage(
                                      `sharedholders.${index}.shareholderPercent`,
                                      formik
                                    )}
                                  />
                                </div>
                              </div>
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              style={{ display: "flex" }}
                              alignItems="center"
                            >
                              {firstSharedholdersItemIndex !== index && (
                                <IconButton
                                  onClick={() => {
                                    formik.setFieldValue(
                                      `sharedholders.${index}.status`,
                                      false
                                    );
                                  }}
                                  disabled={!canEdit || !isDirector}
                                >
                                  <DeleteOutline color="error" />
                                </IconButton>
                              )}
                            </Grid>
                          </React.Fragment>
                        );
                      })}
                    </Grid>

                    <Grid item xs={12}>
                      <AddRowButton
                        disabled={!canEdit || !isDirector}
                        onClick={() => {
                          formik.setFieldValue("sharedholders", [
                            ...Sharedholders,
                            defaultShareholders,
                          ]);
                        }}
                      />
                    </Grid>
                  </Grid>
                </StyledTableCell>
              </TableRow>
              <TableRow>
                <StyledTableCell className={classes.tableCell}>
                  <Grid container className={classes.gridContainer} spacing={4}>
                    <Grid
                      item
                      xs={12}
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                      <h5 className={classes.textLabelBold}>
                        Number of employees (Persons)
                      </h5>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classesField.cellContent}>
                        <div className={classesField.wrapField}>
                          <InputLabel>Foreigners (Persons)</InputLabel>
                          <TextField
                            disabled={!canEdit || !isDirector}
                            className={classesField.textField}
                            variant="outlined"
                            type="number"
                            name="numberForeignEmployee"
                            value={formik.values.numberForeignEmployee}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={isError("numberForeignEmployee", formik)}
                            helperText={getErrorMessage(
                              "numberForeignEmployee",
                              formik
                            )}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classesField.cellContent}>
                        <div className={classesField.wrapField}>
                          <InputLabel>Thai employee (Persons)</InputLabel>
                          <TextField
                            disabled={!canEdit || !isDirector}
                            className={classesField.textField}
                            variant="outlined"
                            type="number"
                            name="numberThaiEmployee"
                            value={formik.values.numberThaiEmployee}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={isError("numberThaiEmployee", formik)}
                            helperText={getErrorMessage(
                              "numberThaiEmployee",
                              formik
                            )}
                          />
                        </div>
                      </div>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                      <p className={classes.textTotalTitle}>
                        Total Number of employees :{" "}
                        <span className={classes.textTotalValue}>
                          {formik.values.numberEmployee}
                        </span>
                      </p>
                    </Grid>
                  </Grid>
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {canEdit && (
          <ButtonController
            hideBack
            saveDraftButtonText="Save"
            onClickSaveDraft={() => {
              setSaveDraft(true);
              callAPISubmit(formik.values);
            }}
            isDisabledDraftButton={!isCurrentShareTotalValid || !isDirector}
            isDisabledNextButton={!isCurrentShareTotalValid || !isDirector}
          />
        )}

        {!canEdit && (
          <ButtonController
            hideBack
            hideSaveDraft
            // onClickNext={() => {

            // }}
          />
        )}
      </form>
    </div>
  );
}
